/* eslint-disable @typescript-eslint/naming-convention */
import { getCaptchaToken } from '@/utils';
import type {
  IOrder,
  IAuthSignupFields,
  IUser,
  IShipmentTrackingInfo,
  ICheckEmailReturnValue,
} from '@/types';
import { BaseApi } from '@/providers';
import { Auth } from '@aws-amplify/auth';

export interface IUserApi {
  checkUserEmail: (email: string) => Promise<boolean>;
  getDocs: () => Promise<IOrder[]> | undefined;
  getShipmentHistory: (id: string) => Promise<IShipmentTrackingInfo> | undefined;
  getUser: () => Promise<IAuthSignupFields> | undefined;
  updateUser: (user: IAuthSignupFields) => Promise<void>;
}
// Todo[UTILS] set getAuth as an imported utils function

const getAuth = async (): Promise<string | undefined> => {
  let authToken;

  try {
    const session = await Auth.currentSession();
    authToken = session.getIdToken().getJwtToken();
  } catch (error) {
    console.log(error);
  }

  return authToken;
};

export class UserApi extends BaseApi implements IUserApi {
  public async getUser(): Promise<IAuthSignupFields> {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    const options = {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      headers: new Headers({ Accept: 'application/json', Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${this.baseApiUrl}/private/me`, options);
    const data = (await response.json()) as unknown;
    if (!response.ok) throw new Error((data as Error).message);
    return data as Promise<IAuthSignupFields>;
  }

  public async getDocs(): Promise<IOrder[]> {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    const options = {
      headers: new Headers({ Accept: 'application/json', Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${this.baseApiUrl}/private/quote`, options);
    const data = (await response.json()) as unknown;
    if (!response.ok) throw new Error((data as Error).message);
    return data as Promise<IOrder[]>;
  }

  public async getShipmentHistory(id: string): Promise<IShipmentTrackingInfo> {
    const response = await fetch(
      `https://api.mbehub.it/tracking?trackingNumber=${id}&countryId=IT&x-debug-log=true`
    );

    if (!response.ok) throw new Error('No tracking information available');

    const [trackingInformations] = (await response.json()) as [IShipmentTrackingInfo];

    return trackingInformations;
  }

  public async updateUser(user: IUser): Promise<void> {
    const recaptchaToken = await getCaptchaToken();
    const authToken = await getAuth();

    let response;

    if (authToken) {
      response = await fetch(`${this.baseApiUrl}/private/me`, {
        method: 'PUT',
        headers: {
          authorization: authToken,
          'Content-Type': 'application/json',
          'X-Recaptcha-Token': recaptchaToken,
        },
        body: JSON.stringify(user),
      });
    }

    if (!response?.ok) throw new Error('Error Updating user');
  }

  public async checkUserEmail(email: string): Promise<boolean> {
    const recaptchaToken = await getCaptchaToken();

    const response = await fetch(`${this.baseApiUrl}/customer/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Recaptcha-Token': recaptchaToken,
      },
      body: JSON.stringify({ email: email }),
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const text: ICheckEmailReturnValue = await response.json();

    if (!response.ok) throw new Error('Error Checking Email');

    return text.exist;
  }
}
